import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { LegacySignupFrame } from '../components/LegacySignupFrame.tsx';
import { useMainContext } from '../context/MainContext.tsx';
import { useMergedConfig } from '../context/MergedConfigProvider';
import { useMParticle } from '../hooks/useMparticle.ts';
import { IdentityStatus } from '../onboarding-api';
import { REGIONS, getRegionForCountry } from '../utils/countriesConfig';
import { getDefaultLanguageForRegion, getDemoSignupJourney } from '../utils/demoSignupJourneyMap.ts';
import logger from '../utils/logger.ts';
import { useIsMobile } from '../utils/useIsMobile.ts';
import { EmailScreen } from './signup/EmailScreen.tsx';
import { OtpScreen } from './signup/OtpScreen.tsx';
import { PasswordScreen } from './signup/PasswordScreen.tsx';
import { RegionSelectScreen } from './signup/RegionSelectScreen.tsx';

enum SIGNUP_STEP {
  REGION = 'REGION',
  EMAIL = 'EMAIL',
  PASSWORD = 'PASSWORD',
  OTP = 'OTP',
}

export type SignUpState = {
  email: string;
  emailStatus?: IdentityStatus;
  password: string;
  requiresSetPassword?: boolean;
  requiresOtp?: boolean;
  otpVerified?: boolean;
  oneTimeToken?: string;
  csrfToken: string;
  connectApplicationId?: string;
  isRegionUnknown?: boolean;
};

const INITIAL_STATE: SignUpState = {
  email: '',
  emailStatus: undefined,
  password: '',
  requiresSetPassword: undefined,
  requiresOtp: undefined,
  otpVerified: undefined,
  oneTimeToken: undefined,
  csrfToken: '',
  isRegionUnknown: true,
};

type Props = {
  onCompleted: (oneTimeToken: string, connectApplicationId?: string, email?: string, password?: string) => void;
};

const createLegacySignupDestination = (region: string, lang: string, isMobile: boolean) => {
  const language = getDemoSignupJourney(region, lang, isMobile) ? lang : getDefaultLanguageForRegion(region, isMobile);
  const journeyId = getDemoSignupJourney(region, language, isMobile);
  const params = new URLSearchParams({ l: language, r: region });

  if (journeyId) params.set('jid', journeyId);
  if (isMobile) {
    params.set('isGateway', 'true');
    params.set('inApp', '1');
  }

  return {
    url: `${import.meta.env.VITE_CMC_REDIRECTION_TO_DEMO_URL}/#?${params}`,
    language,
    journeyId,
  };
};

export const SignUpJourney: FC<Props> = ({ onCompleted }) => {
  const [signUpState, setSignUpState] = useState<SignUpState>(INITIAL_STATE);
  const [legacySignupUrl, setLegacySignupUrl] = useState<string>();
  const { setUserData } = useMParticle();
  const { setCsrfToken } = useMainContext();
  const { lang, isRegionUnknown, pegaStartingFields, region } = useMergedConfig();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const showRegionSelect = isMobile || isRegionUnknown; // Always show region select for mobile, and for desktop if the region is unknown

  const redirectToDemoSignup = useCallback(
    (commercialRegion: string) => {
      const destination = createLegacySignupDestination(commercialRegion, lang, isMobile);

      logger.info(
        `[DEMO_SIGNUP_HANDOFF] Embedding legacy onboarding; region=${commercialRegion}, language=${destination.language}, mobile=${isMobile}, journeyId=${destination.journeyId ?? 'none'}`
      );

      setLegacySignupUrl(destination.url);
    },
    [lang, isMobile]
  );

  const hasCheckedPreResolvedRegionRef = useRef(false);
  useEffect(() => {
    if (hasCheckedPreResolvedRegionRef.current || region !== '' || isRegionUnknown) return;
    hasCheckedPreResolvedRegionRef.current = true;

    const onboardingRegion = pegaStartingFields.OnboardingRegion;
    if (!onboardingRegion) return;

    const commercialRegion = getRegionForCountry(onboardingRegion, lang);
    if (commercialRegion !== REGIONS.UK) {
      redirectToDemoSignup(commercialRegion);
    }
  }, [region, isRegionUnknown, pegaStartingFields.OnboardingRegion, lang, redirectToDemoSignup]);

  const persistSelectedCountry = (countryCode: string) => {
    const params = new URLSearchParams(location.search);
    params.set('region', countryCode.toUpperCase());
    // Delete any incoming language when UK is the selected region
    params.delete('language');
    params.set('l', 'en');
    history.replace({ search: params.toString() });
  };

  const clearSelectedCountry = () => {
    const params = new URLSearchParams(location.search);
    params.delete('region');
    history.replace({ search: params.toString() });
  };
  const step = useMemo(() => {
    if (isRegionUnknown && signUpState.isRegionUnknown !== false) {
      return SIGNUP_STEP.REGION;
    }
    if (!signUpState.emailStatus) {
      return SIGNUP_STEP.EMAIL;
    }
    if (signUpState.requiresOtp && !signUpState.otpVerified) {
      return SIGNUP_STEP.OTP;
    }
    if (!signUpState.password) {
      return SIGNUP_STEP.PASSWORD;
    }
  }, [signUpState, isRegionUnknown]);

  useEffect(() => {
    if (signUpState.oneTimeToken) {
      setCsrfToken(signUpState.csrfToken);

      setUserData({
        email: signUpState.email,
      });

      onCompleted(signUpState.oneTimeToken, signUpState.connectApplicationId, signUpState.email, signUpState.password);
    }
  }, [signUpState, onCompleted, setUserData, setCsrfToken]);

  if (legacySignupUrl) {
    return <LegacySignupFrame url={legacySignupUrl} />;
  }

  return (
    <>
      {step === SIGNUP_STEP.REGION && showRegionSelect && (
        <RegionSelectScreen
          lang={lang}
          onCountrySelected={(country) => {
            const commercialRegion = getRegionForCountry(country.value, lang);
            if (commercialRegion !== REGIONS.UK) {
              redirectToDemoSignup(commercialRegion);
              return;
            }

            persistSelectedCountry(country.value);
            setSignUpState((currentState) => ({
              ...currentState,
              isRegionUnknown: false,
            }));
          }}
        />
      )}
      {step === SIGNUP_STEP.EMAIL && (
        <EmailScreen
          signUpState={signUpState}
          onCompleted={(email, status, csrfToken) => {
            setSignUpState((currentState) => {
              return {
                ...currentState,
                email,
                emailStatus: status,
                csrfToken,
              };
            });
          }}
          onGoBack={
            isRegionUnknown || signUpState.isRegionUnknown === false
              ? () => {
                  clearSelectedCountry();
                  setSignUpState((currentState) => ({
                    ...INITIAL_STATE,
                    email: currentState.email,
                  }));
                }
              : undefined
          }
        />
      )}
      {step === SIGNUP_STEP.PASSWORD && (
        <PasswordScreen
          signUpState={signUpState}
          onGoBack={() => {
            setSignUpState((currentState) => ({
              ...INITIAL_STATE,
              email: currentState.email,
            }));
          }}
          onForgotPassword={(requiresOtp: boolean) => {
            setSignUpState((currentState) => ({
              ...currentState,
              password: '',
              requiresOtp,
            }));
          }}
          onCompleted={(password: string, requiresOtp: boolean, oneTimeToken?: string) => {
            setSignUpState((currentState) => ({
              ...currentState,
              password,
              requiresOtp,
              oneTimeToken,
            }));
          }}
        />
      )}
      {step === SIGNUP_STEP.OTP && (
        <OtpScreen
          signUpState={signUpState}
          onGoBack={() => {
            setSignUpState((currentState) => ({
              ...INITIAL_STATE,
              email: currentState.email,
            }));
          }}
          onCompleted={(requiresSetPassword: boolean, oneTimeToken?: string, connectApplicationId?: string) => {
            setSignUpState((currentState) => ({
              ...currentState,
              otpVerified: true,
              requiresSetPassword,
              oneTimeToken,
              connectApplicationId,
            }));
          }}
        />
      )}
    </>
  );
};
